import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { FullCreditApplication } from '../../types'
import { ElectronicSignature } from '../../types/ElectronicSignature'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const BASE_URL = '/Applications'

// Types
interface ElectronicSignatureParams {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
}

// API Functions
const sendSignatureLink: MutationFunction<ElectronicSignature, ElectronicSignatureParams> = async ({
  financingProgramId,
  creditApplicationId,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<ElectronicSignature>(
    `${BASE_URL}/${financingProgramId}/${creditApplicationId}/ElectronicSignature`,
  )
  return response.data
}

const resendSignatureLink: MutationFunction<ElectronicSignature, ElectronicSignatureParams> = async ({
  financingProgramId,
  creditApplicationId,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<ElectronicSignature>(
    `${BASE_URL}/${financingProgramId}/${creditApplicationId}/ElectronicSignature/ResendLink`,
  )
  return response.data
}

// React-Query Hooks
export function useSendSignatureLink(): [
  MutationFunction<ElectronicSignature, ElectronicSignatureParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: sendSignatureLink,
    onSuccess: (newSignature, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          electronicSignatures: [...currentData.electronicSignatures, newSignature],
        })
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

export function useResendSignatureLink(): [
  MutationFunction<ElectronicSignature, ElectronicSignatureParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: resendSignatureLink,
    onSuccess: (updatedSignature, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          electronicSignatures: currentData.electronicSignatures.map((s) =>
            s.creditApplicationId === updatedSignature.creditApplicationId ? updatedSignature : s,
          ),
        })
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}
